// absolute imports
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

// relative imports
import { AccountConfigProvider } from '../components/providers/AccountConfigProvider';
import { AccountProvider } from '../components/providers/AccountProvider';
import { BillingProvider } from '../components/providers/BillingProvider';
import { ClientSummaryProvider } from '../components/providers/ClientSummaryProvider';
import { ExchangesProvider } from '../components/providers/ExchangesProvider';
import { GlobalErrorProvider } from '../components/providers/GlobalErrorProvider';
import { MonitoringAlertsProvider } from '../components/providers/MonitoringAlertsProvider';
import { ReportProvider } from '../components/providers/ReportProvider';
import { WalletsProvider } from '../components/providers/WalletsProvider';
import IdleTimer from '../components/shared/authentication/IdleTimer';
import ProviderLoadingGuard from '../components/shared/authentication/ProviderLoadingGuard';
import BrowserCheck from '../components/shared/browser-detection/BrowserCheck';
import AccountDisclaimer from '../components/shared/custom-components/AccountDisclaimer';
import CustomZendeskLauncher from '../components/shared/customer-support/CustomZendeskLauncher';
import HelpWidgetOverlay from '../components/shared/customer-support/HelpWidgetOverlay';
import PrimaryLayout from '../components/shared/layouts/PrimaryLayout';
import ModalRenderer from '../components/shared/modals/ModalRenderer';
import LoadingPage from '../components/shared/page-components/LoadingPage';
import RouteAccessGuard from '../components/shared/route-management/RouteAccessGuard';
import {
  currentAccountIdVar,
  primaryAccountIdVar,
  switchingAccountsVar,
} from '../data/apollo/cache/reactiveVars';

function AuthenticatedRouteWrapper() {
  return (
    <GlobalErrorProvider>
      <AccountProvider>
        <AccountConfigProvider>
          <RouteAccessGuard>
            <ClientSummaryProvider>
              <BillingProvider>
                <WalletsProvider>
                  <ExchangesProvider>
                    <MonitoringAlertsProvider>
                      <ReportProvider>
                        <ModalRenderer />
                        <IdleTimer />
                        <BrowserCheck />
                        <AccountDisclaimer />
                        <HelpWidgetOverlay />
                        {!window.Cypress && <CustomZendeskLauncher />}
                        <ProviderLoadingGuard>
                          <PrimaryLayout>
                            <Outlet />
                          </PrimaryLayout>
                        </ProviderLoadingGuard>
                      </ReportProvider>
                    </MonitoringAlertsProvider>
                  </ExchangesProvider>
                </WalletsProvider>
              </BillingProvider>
            </ClientSummaryProvider>
          </RouteAccessGuard>
        </AccountConfigProvider>
      </AccountProvider>
    </GlobalErrorProvider>
  );
}

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedRouteWrapper,
  pendingComponent: () => <LoadingPage loadingPage />,
  beforeLoad: async ({ context: { auth0 }, location, search }) => {
    const { pathname } = location;
    const { user, isLoading, isAuthenticated, getAccessTokenSilently } =
      auth0 || {};

    // Detect if we're navigating away from a client route back to main account
    const currentAccountId = currentAccountIdVar();
    const primaryAccountId = primaryAccountIdVar();
    const isLeavingClientRoute =
      currentAccountId &&
      primaryAccountId &&
      currentAccountId !== primaryAccountId &&
      !pathname.startsWith('/client/');

    if (isLeavingClientRoute) {
      switchingAccountsVar(true);
    } else if (!pathname.startsWith('/client/') && switchingAccountsVar()) {
      // If we're on a non-client route and switching is still true,
      // clear it (handles stuck state from failed/interrupted config loads)
      switchingAccountsVar(false);
    }

    if (
      pathname === '/auth-callback' &&
      search.error_description === 'signup_not_allowed'
    ) {
      window.location.href = 'https://ledgible.io/contact';

      return;
    }

    if (!auth0?.isAuthenticated && !isLoading) {
      if (
        !isAuthenticated &&
        getAccessTokenSilently &&
        pathname !== '/logout'
      ) {
        try {
          await getAccessTokenSilently({
            cacheMode: 'on',
            detailedResponse: true,
            timeoutInSeconds: 10,
          });
        } catch (error) {
          throw redirect({
            code: 401,
            to: '/login',
            search: { appState: { returnTo: pathname } },
          });
        }
      }

      // ENSURE EMAIL IS VERIFIED
      if (
        isAuthenticated &&
        user &&
        !user.email_verified &&
        pathname !== '/verify-email' &&
        pathname !== '/auth-callback'
      ) {
        redirect({
          to: '/verify-email',
          search: (prev) => prev,
        });
      }
    }
  },
});
